<script lang="ts">
import { PropType, VNode, defineComponent, h, resolveComponent } from 'vue';

import { formatCurrency, formatNumber } from '@/utils/filters';
import { Nullable } from '@/utils/types';

import Label from '@/bundles/Common/components/Label.vue';
import BuildingSizeLabel from '@/bundles/MeasurementUnits/components/BuildingSizeLabel.vue';
import LandSizeLabel from '@/bundles/MeasurementUnits/components/LandSizeLabel.vue';

interface IProps {
  totalDeals: Nullable<number>;
  showBrokerGross: boolean;
  reportType: string;
  totalConsideration: Nullable<number>;
  totalBrokerGross: Nullable<number>;
  totalBrokerNet: Nullable<number>;
  totalBrokerNet2: Nullable<string>;
  totalBuildingSize: Nullable<number>;
  totalLandSize: Nullable<number>;
}

export default defineComponent({
  name: 'AgentRankingSummaryMobileCard',

  inheritAttrs: false,

  props: {
    totalDeals: {
      type: Number as PropType<Nullable<number>>,
      default: null
    },
    showBrokerGross: Boolean,
    reportType: {
      type: String,
      default: 'paid'
    },
    totalConsideration: {
      type: Number as PropType<Nullable<number>>,
      default: null
    },
    totalBrokerGross: {
      type: Number as PropType<Nullable<number>>,
      default: null
    },
    totalBrokerNet: {
      type: Number as PropType<Nullable<number>>,
      default: null
    },
    totalBrokerNet2: {
      type: String as PropType<Nullable<string>>,
      default: null
    },
    totalBuildingSize: {
      type: Number as PropType<Nullable<number>>,
      default: null
    },
    totalLandSize: {
      type: Number as PropType<Nullable<number>>,
      default: null
    },
  },

  render (): VNode | VNode[] {
    const props = this.$props as IProps;

    const VCard = resolveComponent('v-card');
    const VSpacer = resolveComponent('v-spacer');
    const VBtn = resolveComponent('v-btn');
    const VIcon = resolveComponent('v-icon');
    const VDivider = resolveComponent('v-divider');

    return h(VCard, {
      variant: 'outlined',
      ...this.$attrs,
      class: ['agent-ranking-summary-mobile-card', this.$attrs.class],
    }, { default: () => [
      h('div', { class: 'd-flex align-center' }, [
        h('span', { class: 'pa-3 agent-ranking-summary-mobile-card__name font-weight-bold' }, 'Agent Ranking Details'),
        h(VSpacer),
        h(VBtn, {
          icon: true,
          size: 'small',
          class: 'mr-2',
          onClick: () => {
            (this.$attrs['onClick:close'] as Function)()
          }
        }, { default: () => [
          h(VIcon, { size: 'small' }, { default: () => 'mdi-close' })
        ] }),
      ]),
      h(VDivider),
      h('div', { class: 'd-flex gap-2 flex-column pa-3' }, [
        h('div', { class: 'd-flex gap-3' }, [
          h('div', { class: 'agent-ranking-summary-mobile-card__info' }, [
            h('div', { class: 'agent-ranking-summary-mobile-card__info-label agent-ranking-summary-mobile-card__deals-label' }, '# of Deals:'),
            h('div', { class: 'agent-ranking-summary-mobile-card__deals' }, [
              h(Label, { value: formatNumber(props.totalDeals) })
            ])
          ]),
          h('div', { class: 'agent-ranking-summary-mobile-card__info' }, [
            h('div', { class: 'agent-ranking-summary-mobile-card__info-label agent-ranking-summary-mobile-card__consideration-label' }, 'Total Consideration:'),
            h('div', { class: 'agent-ranking-summary-mobile-card__consideration' }, [
              h(Label, { value: formatCurrency(props.totalConsideration) })
            ])
          ])
        ]),
        h('div', { class: 'd-flex gap-3' }, [
          h('div', { class: 'agent-ranking-summary-mobile-card__info' }, [
            h('div', { class: 'agent-ranking-summary-mobile-card__info-label agent-ranking-summary-mobile-card__building-size-label' }, 'Total Bldg Size:'),
            h('div', { class: 'agent-ranking-summary-mobile-card__building-size' }, [
              h(BuildingSizeLabel, { value: props.totalBuildingSize })
            ])
          ]),
          h('div', { class: 'agent-ranking-summary-mobile-card__info' }, [
            h('div', { class: 'agent-ranking-summary-mobile-card__info-label agent-ranking-summary-mobile-card__land-size-label' }, 'Total Land Size:'),
            h('div', { class: 'agent-ranking-summary-mobile-card__land-size' }, [
              h(LandSizeLabel, { value: props.totalLandSize })
            ])
          ])
        ]),
        props.showBrokerGross ? h('div', { class: 'd-flex flex-column gap-3' }, [
          h('div', { class: 'agent-ranking-summary-mobile-card__info' }, [
            h('div', { class: 'agent-ranking-summary-mobile-card__info-label agent-ranking-summary-mobile-card__agent-gross-label' }, 'Agent Gross:'),
            h('div', { class: 'agent-ranking-summary-mobile-card__agent-gross' }, [
              h(Label, { value: formatCurrency(props.totalBrokerGross) })
            ])
          ]),
          h('div', { class: 'agent-ranking-summary-mobile-card__info' }, [
            h('div', { class: 'agent-ranking-summary-mobile-card__info-label agent-ranking-summary-mobile-card__agent-net-label' }, 'Agent Net:'),
            h('div', { class: 'agent-ranking-summary-mobile-card_agent' }, [
              h(Label, { value: props.reportType === 'paid' ? formatCurrency(props.totalBrokerNet) : null })
            ])
          ]),
          h('div', { class: 'agent-ranking-summary-mobile-card__info' }, [
            h('div', { class: 'agent-ranking-summary-mobile-card__info-label agent-ranking-summary-mobile-card__vacancy-status-label' }, 'Broker Net:'),
            h('div', { class: 'agent-ranking-summary-mobile-card__vacancy-status' }, [
              h(Label, { value: props.totalBrokerNet2 })
            ])
          ])
        ]) : null,
      ]),
    ] })
  }
})
</script>

<!-- Cannot be scoped due to strange style behaviour, styles applied not to all divs -->
<style lang="scss">
.agent-ranking-summary-mobile-card {
  .agent-ranking-summary-mobile-card__info {
    flex: 1 1 50% !important;
    line-height: 18px !important;
    font-size: 13px !important;
  }

  .agent-ranking-summary-mobile-card__info-label {
    font-weight: 600 !important;
  }
}
</style>
